import { Back, Sine, TimelineLite } from "gsap";
import * as HLF from "html-living-framework";
import { Application } from "../Application";
import { AppProgress } from "../Data/AppProgress";
import * as Constants from "../Data/Constants";
import { Regions, RegionType } from "../Data/Regions";
import * as SFX from "../Data/SoundEffectKeys";
import * as VoiceOver from "../Data/VoiceOverKeys";
import { Button } from "../GameObjects/Button";
import { Fabuloso } from "../GameObjects/Fabuloso";
import { HealthBar } from "../GameObjects/HealthBar";
import { KeyboardFocusManager } from "../GameObjects/KeyboardFocusManager";
import { InteruptType, VoiceCategory } from "../GameObjects/VoiceOverManager";

export enum ResultType {
    SUCCESS,
    FAIL,
    COMPLETE,
    NUM_ELEMENTS,
}

export class Results extends HLF.State {
    private static funFactIndeces: number[] = [ 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13 ];
    private static activeFunFactIndex: number = -1;

    private _resultType: ResultType;
    private _voKey: string;
    private _sfxKey: string;

    private _bg: PIXI.Sprite;
    private _glow: PIXI.Sprite;
    private _fabuloso: Fabuloso;
    private _button: Button;
    private _ticket: PIXI.Sprite | undefined;
    private _tickets: PIXI.Sprite[];
    private _overlay: PIXI.Sprite;
    private _keyboardFocusManager: KeyboardFocusManager;
    private _healthBar: HealthBar | undefined;

    private _timeline: TimelineLite | undefined;
    private _voCompleteCallback: () => void;

    constructor() {
        super();
        if (AppProgress.currentLevelCompleted === false) {
            this._resultType = ResultType.FAIL;
        }
        else {
            if (AppProgress.levelsCompleted % 8 < 7) {
                this._resultType = ResultType.SUCCESS;
            }
            else {
                this._resultType = ResultType.COMPLETE;
            }
        }

        this._bg = HLF.PixiUtils.makeSprite("results-bg");
        this.addChild(this._bg);

        this._glow = HLF.PixiUtils.makeSprite("results-glow", Constants.SPRITESHEET_UI);
        this._glow.scale.set(2);
        this.addChild(this._glow);

        switch (this._resultType) {
            case ResultType.SUCCESS:
                this._voKey = VoiceOver.WIN_PREFIX + VoiceOver.REGION_SUFFIX[AppProgress.activeLevel];
                this._sfxKey = SFX.LEVEL_SUCCESS;
                break;
            case ResultType.COMPLETE:
                this._voKey = VoiceOver.WIN_CIRCUS;
                this._sfxKey = SFX.LEVEL_SUCCESS;
                break;
            case ResultType.FAIL:
            default:
                this._voKey = HLF.ArrayUtils.random(VoiceOver.LOSE);
                this._sfxKey = SFX.GAME_OVER;
                break;
        }
        Application.instance.audio.load(this._voKey, HLF.AudioCategory.VO);

        this._fabuloso = new Fabuloso();
        this._fabuloso.scale.x = this._fabuloso.scale.x * -1;
        this.addChild(this._fabuloso);

        this._tickets = new Array<PIXI.Sprite>(RegionType.NUM_ELEMENTS);
        const ticketSpacing: number = 120;
        if (this._resultType === ResultType.COMPLETE) {
            for (let i = 0; i < this._tickets.length; ++i) {
                this._tickets[i] = HLF.PixiUtils.makeSprite(Regions.data[i].ticket.asset, Regions.data[i].ticket.sheet);
                this._tickets[i].position.set(
                    (-((this._tickets.length - 1) / 2) + i) * ticketSpacing,
                    -100,
                );
                this._tickets[i].visible = false;
                this.addChild(this._tickets[i]);
            }
        }
        else if (this._resultType === ResultType.SUCCESS) {
            this._ticket = HLF.PixiUtils.makeSprite(
                Regions.data[AppProgress.activeLevel].ticket.asset,
                Regions.data[AppProgress.activeLevel].ticket.sheet,
            );
            this.addChild(this._ticket);
        }
        else if (this._resultType === ResultType.FAIL) {
            this._healthBar = new HealthBar(true);
            this._healthBar.setHealth(0, true);
            this.addChild(this._healthBar);
        }

        if (this._resultType === ResultType.FAIL) {
            this._button = new Button(
                this.onButtonReplayPressed.bind(this),
                "button-replay", Constants.SPRITESHEET_UI,
                VoiceOver.TRY_AGAIN,
                SFX.UI_SELECT,
            );
        }
        else {
            this._button = new Button(
                this.onButtonContinuePressed.bind(this),
                "button-play", Constants.SPRITESHEET_UI,
                VoiceOver.NEXT,
                SFX.UI_SELECT,
            );
        }
        this._button.position.set(0, 300);
        this._button.autoPulse = true;
        this.addChild(this._button);

        this._keyboardFocusManager = new KeyboardFocusManager();
        this.addChild(this._keyboardFocusManager);

        this._overlay = HLF.PixiUtils.makeSprite("white-pixel", Constants.SPRITESHEET_UI);
        this._overlay.tint = 0x000000;
        this.addChild(this._overlay);

        PubSub.publishSync(HLF.PUSH_KEYBOARD_LAYER, undefined);

        this._voCompleteCallback = this.onIntroVOComplete.bind(this);
    }

    public static getRequiredAssets(): string[] {
        const requiredAssets: string[] = [
            "results-bg" + HLF.ResourceUtils.FILE_EXTENSION_JPG,
            Constants.SPRITESHEET_TERRAIN_PREFIX + "common" + HLF.ResourceUtils.FILE_EXTENSION_JSON,
        ];
        return requiredAssets;
    }

    public update(pDeltaTime: number): void {
        this._fabuloso.update(pDeltaTime);
    }

    public onResize(pSize: PIXI.Point): void {
        super.onResize(pSize);

        this._fabuloso.position.set(
            -this._size.x * 0.5 + 150,
            this._size.y * 0.5,
        );
        this._overlay.scale.set(this._size.x, this._size.y);
    }

    public animateIn(pOnComplete: () => void): void {
        pOnComplete();

        this._button.position.y = this._size.y * 0.5 + this._button.height * 0.5;
        this._overlay.alpha = 1;
        this.scale.set(1.1);

        if (this._timeline !== undefined) { this._timeline.kill(); }
        this._timeline = new TimelineLite();

        this._timeline.to(this.scale, 1.0, {
            x: 1,
            y: 1,
            ease: Sine.easeInOut,
        }, 0);
        this._timeline.to(this._overlay, 0.75, {
            alpha: 0,
            ease: Sine.easeInOut,
        }, 0);
        if (this._resultType === ResultType.COMPLETE) {
            for (let i = 0; i < this._tickets.length; ++i) {
                this._tickets[i].position.y = 100;
                this._tickets[i].alpha = 0;
                this._tickets[i].visible = true;
                const time: number = 0.5 + 0.075 * i;
                this._timeline.to(this._tickets[i], 1.0, {
                    y: -100,
                    ease: Back.easeOut,
                }, time);
                this._timeline.to(this._tickets[i], 0.75, {
                    alpha: 1,
                    ease: Sine.easeInOut,
                }, time + 0.1);
                this._timeline.call(() => {
                    Application.instance.audio.play(HLF.ArrayUtils.random(SFX.WOOSH), 1, false, HLF.AudioCategory.SFX);
                }, undefined, this, time + 0.3);
            }
        }
        else if (this._resultType === ResultType.SUCCESS && this._ticket !== undefined) {
            this._ticket.alpha = 0;
            this._ticket.position.set(175, -65);
            this._ticket.rotation = 0.5;
            this._timeline.to(this._ticket, 0.5, {
                alpha: 1,
                ease: Sine.easeInOut,
            }, 0.75);
            this._timeline.to(this._ticket, 0.7, {
                x: 0,
                ease: Sine.easeOut,
            }, 0.75);
            this._timeline.to(this._ticket, 0.7, {
                rotation: 0,
                ease: Sine.easeOut,
            }, 0.75);
        }
        else if (this._resultType === ResultType.FAIL && this._healthBar !== undefined) {
            this._healthBar.alpha = 0;
            this._healthBar.scale.set(1.3);

            this._timeline.to(this._healthBar, 0.5, {
                alpha: 1,
                ease: Sine.easeInOut,
            }, 0.75);
            this._timeline.to(this._healthBar.scale, 0.5, {
                x: 1,
                y: 1,
                ease: Sine.easeInOut,
            }, 0.75);
            this._timeline.call(() => {
                this._healthBar!.playPulse();
            }, undefined, this, 1.25);
        }
        this._timeline.call(() => {
            Application.instance.audio.play(this._sfxKey, 1, false, HLF.AudioCategory.SFX);
        }, undefined, this, 0.5);
        this._timeline.call(() => {
            this._fabuloso.playResultsAnim(this._resultType);
            Application.instance.voiceOver.play(this._voKey, InteruptType.EQUAL, VoiceCategory.INSTRUCTIONAL);
            Application.instance.audio.getAudioTrack(this._voKey, HLF.AudioCategory.VO)!.once(
                HLF.HowlerUtils.Events.END,
                this._voCompleteCallback,
            );
        }, undefined, this, 1.0);

        this._timeline.to(this._button, 0.5, {
            y: this._size.y * 0.5 - this._button.height * 0.5 - 40,
            ease: Back.easeOut,
        }, 1.5);
        this._timeline.call(() => {
            PubSub.publishSync(HLF.PUSH_KEYBOARD_LAYER, undefined);
            PubSub.publishSync(HLF.REGISTER_FOCUSABLE, this._button);
        }, undefined, this, 2.0);
        this._timeline.call(() => {
            this._button.playPulse();
        }, undefined, this, 2.5);
    }

    public animateOut(pOnComplete: () => void): void {
        if (this._timeline !== undefined) { this._timeline.kill(); }
        this._timeline = new TimelineLite();

        if (this._resultType === ResultType.COMPLETE) {
            this._timeline.call(() => {
                this._fabuloso.playSelectionAnim();
                Application.instance.audio.play(SFX.TICKET_EARNED, 1, false, HLF.AudioCategory.SFX);
            });
            for (let i = 0; i < this._tickets.length; ++i) {
                const time: number = 0.1 * i;
                this._timeline.to(this._tickets[i], 0.5, {
                    y: -200 - 7 * i,
                    ease: Sine.easeOut,
                }, time);
                this._timeline.to(this._tickets[i], 0.5, {
                    y: 230,
                    ease: Sine.easeIn,
                }, time + 0.5);
                this._timeline.to(this._tickets[i], 1.0, {
                    x: -this._size.x * 0.5 + 310,
                    ease: Sine.easeInOut,
                }, time);
                this._timeline.to(this._tickets[i].scale, 1.0, {
                    x: 0.2,
                    y: 0.2,
                    ease: Sine.easeIn,
                }, time);
                this._timeline.to(this._tickets[i], 0.15, {
                    alpha: 0,
                    ease: Sine.easeInOut,
                }, time + 0.85);
            }
        }
        else if (this._resultType === ResultType.FAIL) {
            this._timeline.call(() => {
                this._healthBar!.stopPulse();
                this._healthBar!.swapHeart(false);
                this._healthBar!.setHealth(1);
            }, undefined, this, 0);
            this._timeline.call(() => { /* dummy call */ }, undefined, this, 1.5);
        }
        const dur: number = this._timeline.duration();
        this._timeline.to(this.scale, 1.5, {
            x: 1.1,
            y: 1.1,
            ease: Sine.easeIn,
        }, Math.max(0, dur - 1.0));
        this._timeline.to(this._overlay, 0.75, {
            alpha: 1,
            ease: Sine.easeInOut,
        }, Math.max(0.25, dur - 0.75));
        this._timeline.call(pOnComplete);
    }

    public destroy(pOptions: boolean | PIXI.DestroyOptions = { children: true }): void {
        if (this._timeline !== undefined) { this._timeline.kill(); }
        delete this._voCompleteCallback;
        super.destroy(pOptions);
    }

    private playFunFact(): void {
        // When playing the first fact, shuffle the fact array
        if (Results.activeFunFactIndex === -1) {
            HLF.ArrayUtils.shuffle(Results.funFactIndeces);
            Results.activeFunFactIndex = 0;
        }
        Application.instance.voiceOver.play(
            VoiceOver.FF_GEN_PREFIX + HLF.NumberUtils.addZeroPadding(Results.activeFunFactIndex, 2),
            InteruptType.LESS,
            VoiceCategory.FUN_FACT,
        );
        Results.activeFunFactIndex = (Results.activeFunFactIndex + 1) % Results.funFactIndeces.length;
    }

    private onIntroVOComplete(): void {
        if (this._resultType === ResultType.SUCCESS) {
            this.playFunFact();
        }
    }

    private onButtonReplayPressed(): void {
        this._button.interactive = false;
        this.clearQueuedVO();
        PubSub.publishSync(HLF.POP_KEYBOARD_LAYER, undefined);
        PubSub.publishSync(HLF.STATE_TRANSITION, new HLF.StateToken(
            Constants.STATE_GAME,
            Constants.LOAD_INTERSTITIAL,
        ));
    }

    private onButtonContinuePressed(): void {
        this._button.interactive = false;
        this.clearQueuedVO();
        PubSub.publishSync(HLF.POP_KEYBOARD_LAYER, undefined);
        AppProgress.levelsCompleted++;
        AppProgress.checkpointReached = false;
        PubSub.publishSync(HLF.STATE_TRANSITION, new HLF.StateToken(
            this._resultType === ResultType.COMPLETE ? Constants.STATE_CIRCUS : Constants.STATE_SELECTION,
            Constants.LOAD_INTERSTITIAL,
        ));
    }

    private clearQueuedVO(): void {
        Application.instance.audio.getAudioTrack(this._voKey, HLF.AudioCategory.VO)!.off(
            HLF.HowlerUtils.Events.END,
            this._voCompleteCallback,
        );
    }
}
